import { COLORS } from './constants-colors';
import * as DataController from './controllers/data_controller';
import {
  anchoredPos,
  getAbsolutePosAndScale,
  getZeroStaticEntity,
  moveEntityToBack,
  setEntityPosition,
  setParent,
} from './entity';
import { time } from './index';
import { createAndAddParticleSystem, fadeOutParticle, getZeroParticle } from './particle_system';
import { mouseDownThisFrame, setVisibilityState } from './simulate';
import {
  createPerlinController,
  Ease,
  getImageUriBySize,
  random,
  randomPosOnBorder,
  randomV2,
  remapNumber,
  remapNumber01,
  removeDenseArrayIndex,
  v2,
} from './helpers';
import {
  DeltaTime,
  VisibilityState,
  GameManager,
  MoveMode,
  Particle,
  Shape,
  type ImageEnt,
  type ParticleSystem,
  type Container,
  type BackgroundLayer,
} from './types';
import { ServerEvent_Background } from './shared/api_shared_types';
import logger from './controllers/logger';
import { createAndAddLocalContainer } from './container';
import { createImageEnt } from './image_entity';

const MOVING_BACKGROUND = false;

const MIN_COUNT = 20;
const MAX_COUNT = 80;
const MAX_SIZE = 40;
const MIN_SIZE = 4;
const MAX_ALPHA = 0.5;
const MIN_ALPHA = 0.07;

let enabled = false;
let activeUri: string = '';
let particleSystem: ParticleSystem = null;
let centerContainer: Container = null;
let container: Container = null;
let imLayer: HTMLImageElement = null;
let layers: BackgroundLayer[] = [];
let videoLayer: BackgroundLayer = null;
let previousScale: number = 1;
let paused = false;
let image: ImageEnt = null;
let pausers: Set<number> = new Set<number>();

export function initBackground(manager: GameManager, startUri = '') {
  {
    const c = createAndAddLocalContainer(manager, getZeroStaticEntity());
    c.width = 500;
    c.height = 500;
    c.relativeWidth = 1;
    c.relativeHeight = 1;
    c.moveMode = MoveMode.ANCHORED;
    c.anchoredPos = anchoredPos(v2(0, 0), v2(0, 0), v2(0, 0));
    c.scale = 0.5;
    setParent(c, manager.spaceBG, v2());
    centerContainer = c;
  }

  {
    const c = createAndAddLocalContainer(manager, getZeroStaticEntity());
    c.width = 500;
    c.height = 500;

    setParent(c, centerContainer, v2());
    container = c;
  }

  if (MOVING_BACKGROUND) {
    imLayer = document.createElement('img') as HTMLImageElement;
    imLayer.style.position = 'fixed';
    manager.DOM.backgroundContainer.appendChild(imLayer);
  }

  if (startUri.length) {
    // setBackground(manager, startUri);
  }
}

export function objectRequestPauseBackground(id: number) {
  pausers.add(id);

  paused = true;
}

export function objectReleasePauseBackground(id: number) {
  pausers.delete(id);

  if (pausers.size === 0) {
    paused = false;
  }
}

export function isPaused(): boolean {
  return paused;
}

export function getContainer(): Container {
  return container;
}

export function updateBackground(manager: GameManager, dt: DeltaTime) {
  for (let i = layers.length - 1; i >= 0; i--) {
    const l = layers[i];
    l.lifeTime += dt;

    const newBackgroundFadeInComplete = layers.some((it) => {
      return it !== l && it.lifeTime < l.lifeTime && it.lifeTime > 1;
    });

    if (newBackgroundFadeInComplete) {
      l.doKill = true;
    }

    if (l.doKill) {
      l.el.remove();
      removeDenseArrayIndex(layers, i);
      continue;
    }
  }

  if (imLayer) {
    const [pos, scale] = getAbsolutePosAndScale(container);
    const aspect = imLayer.naturalWidth / imLayer.naturalHeight;
    const w = 100 * scale * 200;
    const h = (100 / aspect) * scale * 200;
    imLayer.style.width = `${w}px`;
    imLayer.style.height = `${h}px`;
    const x = pos.x - w / 2.5;
    const y = pos.y - h / 2;

    const blurstrength = remapNumber(scale, 0.15, 0.5, 0, 10, true);

    imLayer.style.filter = `blur(${blurstrength}px)`;

    imLayer.style.transform = `translateX(${x}px) translateY(${y}px) `;
  }

  if (enabled) {
    if (particleSystem) {
      const ps = particleSystem;

      ps.paused = paused;

      const [pos, scale] = getAbsolutePosAndScale(ps);

      const scaleDiff = scale - previousScale;
      const zoomIn = scaleDiff > 0.000001;

      const boundaryLeft = Math.min(-pos.x / scale, -200);
      const boundaryRight = Math.max((manager.space.width - pos.x) / scale, 200);
      const boundaryTop = Math.min(-pos.y / scale, -200);
      const boundaryBottom = Math.max((manager.space.height - pos.y) / scale, 200);

      const minParticleScale = Math.min(MIN_SIZE / (scale * scale), MAX_SIZE - 4);

      const p = 1 - remapNumber01(scale, 0.2, 2, true);
      const pe = Ease.inCubic(p);
      const count = remapNumber(pe, 0, 1, MIN_COUNT, MAX_COUNT, true);

      for (let i = ps.particles.length - 1; i >= 0; i--) {
        if (ps.particles[i].isDisappearing) continue;

        const p = ps.particles[i];
        if (p.size < minParticleScale) {
          if (ps.particles.length > count && !ps.particles[i].isDisappearing) {
            //remove particle
            fadeOutParticle(p, 1);
            continue;
          } else {
            p.size = random(minParticleScale, MAX_SIZE);
            p.pos = randomPosOnBorder(boundaryLeft, boundaryRight, boundaryTop, boundaryBottom);
          }
        }

        if (i > count && !ps.particles[i].isDisappearing) {
          fadeOutParticle(ps.particles[i], 1);
          continue;
        }

        if (p.pos.x < boundaryLeft - p.size) {
          p.size = zoomIn ? minParticleScale : random(minParticleScale, MAX_SIZE);
          p.pos.x = zoomIn ? random(boundaryLeft, boundaryRight) : boundaryRight + p.size;
          p.pos.y = random(boundaryTop, boundaryBottom);
        } else if (p.pos.x > boundaryRight + p.size) {
          p.size = zoomIn ? minParticleScale : random(minParticleScale, MAX_SIZE);
          p.pos.x = zoomIn ? random(boundaryLeft, boundaryRight) : boundaryLeft - p.size;
          p.pos.y = random(boundaryTop, boundaryBottom);
        }
        if (p.pos.y < boundaryTop - p.size) {
          p.size = zoomIn ? minParticleScale : random(minParticleScale, MAX_SIZE);
          p.pos.y = zoomIn ? random(boundaryTop, boundaryBottom) : boundaryBottom + p.size;
          p.pos.x = random(boundaryLeft, boundaryRight);
        } else if (p.pos.y > boundaryBottom + p.size) {
          p.size = zoomIn ? minParticleScale : random(minParticleScale, MAX_SIZE);
          p.pos.y = zoomIn ? random(boundaryTop, boundaryBottom) : boundaryTop - p.size;
          p.pos.x = random(boundaryLeft, boundaryRight);
        }
      }

      if (ps.particles.length < count) {
        const maxAlpha = random(MIN_ALPHA, MAX_ALPHA);

        for (let i = ps.particles.length; i < count; i++) {
          let p: Particle;

          p = {
            ...getZeroParticle(),
            pos: randomV2(boundaryLeft, boundaryRight, boundaryTop, boundaryBottom),
            size: random(minParticleScale, MAX_SIZE),
            rot: random(-10, 10),
            dragCoefficient: 0.05,
            shape: Shape.NONE,
            spriteSheet: ps.spriteSheet,
            spriteIndex: DataController.getRandomSpriteIndex(ps.spriteSheet),
            animationTime: random(0, 0.5),
            wanderSpeed: random(5, 30),
            transparancyPerlin: createPerlinController(maxAlpha / 10, maxAlpha, 0.4),
          };
          p.shapeStyle.fillColor = COLORS.WHITE_100;
          ps.particles.push(p);
        }
      }

      if (mouseDownThisFrame()) {
        particleSystem.influencers.push({
          pos: ps.relativeMousePos,
          parentEntity: null,
          radius: 200,
          outerStrength: 50000,
          innerStrength: 0,
          singleBurst: true,
        });
      }

      ps.influencers[0].pos = ps.relativeMousePos;

      previousScale = scale;
    }
  }
}

export async function syncBackground(manager: GameManager, data: ServerEvent_Background) {
  if (data.videoUri) {
    setVideoBackground(manager, data.videoUri);
  } else {
    if (videoLayer) {
    }
  }

  if (data.uri === activeUri) {
    return;
  }

  if (data.uri.substring(0, 4) === 'http') {
    const uriLq = getImageUriBySize(data.uri, 'small');
    const uriHq = getImageUriBySize(data.uri, 'large');
    let [successLq, imgLq] = DataController.getImage(uriLq, true);
    let [successHq, imgHq] = DataController.getImage(uriHq, true);

    if (successHq) {
      setBackground(manager, imgHq);
      // if (MOVING_BACKGROUND) {
      //   setBackgroundNew(manager, uriHq);
      // }
    } else if (successLq) {
      setBackground(manager, imgLq);
      // if (MOVING_BACKGROUND) {
      //   setBackgroundNew(manager, uriLq);
      // }
    } else {
      //Load fast low quality image

      // if (MOVING_BACKGROUND) {
      //   DataController.cacheImageFromApiAsync(manager.user.token, uriHq, uriHq);
      //   setBackgroundNew(manager, uriHq);
      // } else {
      await DataController.cacheImageFromApiAsync(
        manager.user.accessToken,
        uriLq,
        uriLq,
        (img) => {
          setBackground(manager, img);
        },
        false,
        true,
      );
      // }
      if (!successHq) {
        //Load slower high quality image
        await DataController.cacheImageFromApiAsync(
          manager.user.accessToken,
          uriHq,
          uriHq,
          (img) => {
            setBackground(manager, img);
          },
          false,
          true,
        );
      }
    }
  } else {
    setBackground(manager, data.uri);
    // if (MOVING_BACKGROUND) {
    //   setBackgroundNew(manager, data.uri);
    // }
  }

  activeUri = data.uri;
}

export function setVideoBackground(manager: GameManager, videoUrl: string) {
  const newBackground = document.createElement('div');
  newBackground.classList.add('background-video-layer');
  newBackground.id = 'background-video-layer-' + time.toString();

  const videoplayer = document.createElement('video') as HTMLVideoElement;
  videoplayer.style.width = '100%';
  videoplayer.autoplay = true;
  videoplayer.src = 'http://127.0.0.1:5131/api/uploads/c2d43c1e4ea5f2a22a350bb0e1efd513.mp4';

  newBackground.appendChild(videoplayer);
  manager.DOM.backgroundVideoContainer.appendChild(newBackground);
  videoLayer = {
    el: newBackground,
    lifeTime: 0,
    doKill: false,
  };
}

export function setBackgroundNew(manager: GameManager, imageId: string) {
  if (image) {
    setVisibilityState(image, VisibilityState.DISAPPEARING);
    image = null;
  }

  if (true) {
    const imProps: Partial<ImageEnt> = {
      disableAutoSize: true,
      allowHighlight: true,
      passHover: true,
      passClick: true,
      minWidthEm: 150,
      maxWidthEm: 150,
    };
    const im = createImageEnt(manager, getZeroStaticEntity(), imageId, imProps);
    setParent(im, container, v2());
    moveEntityToBack(im);
    image = im;
  }
}

export function setBackground(manager: GameManager, image: string | HTMLImageElement) {
  if (typeof image === 'string') {
    if (activeUri === image) return;
    activeUri = image;
  }

  enabled = false;

  const newBackground = document.createElement('div');
  newBackground.classList.add('background-layer');
  newBackground.id = 'background-layer-' + time.toString();
  if (typeof image === 'string') {
    if (MOVING_BACKGROUND) {
      imLayer.src = image;
    } else {
      newBackground.style.backgroundImage = `url(${image})`;
    }
  } else {
    if (MOVING_BACKGROUND) {
      imLayer.src = image.src;
    } else {
      newBackground.style.backgroundImage = `url(${image.src})`;
    }
  }
  manager.DOM.backgroundContainer.appendChild(newBackground);
  layers.push({
    el: newBackground,
    lifeTime: 0,
    doKill: false,
  });

  if (!particleSystem) {
    const ent = getZeroStaticEntity();

    const ps = createAndAddParticleSystem(manager, ent);

    ps.width = 10;
    ps.height = 10;

    const maxAlpha = random(MIN_ALPHA, MAX_ALPHA);

    const spriteSheet = DataController.getSpriteSheet('spritesheet_soft_white');
    ps.spriteSheet = spriteSheet;

    ps.influencers.push({
      pos: ps.relativeMousePos,
      parentEntity: null,
      radius: 80,
      outerStrength: 700,
      innerStrength: 0,
      singleBurst: false,
    });

    setVisibilityState(ps, VisibilityState.DEFAULT);
    particleSystem = ps;
    setParent(ps, container);
    setEntityPosition(ps, 0, 0);
    const [pos, scale] = getAbsolutePosAndScale(ps);

    previousScale = scale;

    const boundaryLeft = Math.min(-pos.x / scale, -200);
    const boundaryRight = Math.max((manager.space.width - pos.x) / scale, 200);
    const boundaryTop = Math.min(-pos.y / scale, -200);
    const boundaryBottom = Math.max((manager.space.height - pos.y) / scale, 200);

    const minParticleScale = MIN_SIZE / scale;

    const p = 1 - remapNumber01(scale, 0.2, 2, true);
    const pe = Ease.inCubic(p);
    const count = remapNumber(pe, 0, 1, MIN_COUNT, MAX_COUNT, true);

    for (let i = 0; i < count; i++) {
      let p: Particle;

      p = {
        ...getZeroParticle(),
        pos: randomV2(boundaryLeft, boundaryRight, boundaryTop, boundaryBottom),
        size: random(minParticleScale, MAX_SIZE),
        rot: random(-10, 10),
        dragCoefficient: 0.05,
        shape: Shape.NONE,
        spriteSheet: spriteSheet,
        spriteIndex: DataController.getRandomSpriteIndex(spriteSheet),
        animationTime: random(0, 0.5),
        wanderSpeed: random(5, 30),
        transparancyPerlin: createPerlinController(maxAlpha / 10, maxAlpha, 0.4),
      };
      p.shapeStyle.fillColor = COLORS.WHITE_100;
      ps.particles.push(p);
    }
  }
}

export function setPadding(width: number, height: number) {
  centerContainer.padWidth = width;
  centerContainer.padHeight = height;
}
